<template>
    <AppSection>
        <el-form ref="formRef" :model="form" :rules="rules" class="text-left">
            <div v-if="form.licenseKey != null" class="mb-1 label--text">
                <div class="mb-1 label--text">License key</div>
                <el-form-item>
                    {{ form.licenseKey }}
                </el-form-item>
            </div>
            <div class="mb-1 label--text">Organization name</div>
            <el-form-item prop="organizationName">
                <el-input
                    v-model="form.organizationName"
                    placeholder="Organization name"
                ></el-input>
            </el-form-item>
            <div class="mb-1 label--text">Max Api access per organization</div>
            <el-form-item prop="maxNumberOfAccessedApisPerOrganization">
                <el-input-number
                    v-model="form.maxNumberOfAccessedApisPerOrganization"
                    :min="-1"
                    :max="2147483647"
                    controls-position="right"
                />
            </el-form-item>
            <div class="mb-1 label--text">
                Max files size per organization (GB)
            </div>
            <el-form-item prop="maxFilesSizePerOrganization">
                <el-input-number
                    v-model="form.maxFilesSizePerOrganization"
                    :min="-1"
                    :max="2147483647"
                    controls-position="right"
                />
            </el-form-item>
            <div class="mb-1 label--text">Max number of eP2AS-Vue users</div>
            <el-form-item prop="maxNumberOfEp2asVueUsers">
                <el-input-number
                    v-model="form.maxNumberOfEp2asVueUsers"
                    :min="-1"
                    :max="2147483647"
                    controls-position="right"
                />
            </el-form-item>
            <div class="mb-1 label--text">Max number of submission units</div>
            <el-form-item prop="maxNumberOfSubmissionUnits">
                <el-input-number
                    v-model="form.maxNumberOfSubmissionUnits"
                    :min="-1"
                    :max="2147483647"
                    controls-position="right"
                />
            </el-form-item>
            <div class="mb-1 label--text">Allowed eCTD versions</div>
            <el-form-item prop="allowedEctdVersions">
                <el-checkbox-group v-model="form.allowedEctdVersions">
                    <el-checkbox value="v3.2.2">v3.2.2</el-checkbox>
                    <el-checkbox value="v4.0">v4.0</el-checkbox>
                </el-checkbox-group>
            </el-form-item>
            <div class="mb-1 label--text">Allowed remote repositories</div>
            <el-form-item prop="allowedRemoteRepositories">
                <el-checkbox-group v-model="form.allowedRemoteRepositories">
                    <el-checkbox value="wfs_smb_v2_v3">SMB v3</el-checkbox>
                    <el-checkbox value="sharepoint"
                        >Sharepoint with ACS</el-checkbox
                    >
                    <el-checkbox value="sharepoint_entraid"
                        >Sharepoint with Entra ID</el-checkbox
                    >
                    <el-checkbox value="documentum">Documentum</el-checkbox>
                    <el-checkbox value="veeva_vault">Veeva Vault</el-checkbox>
                    <el-checkbox value="agatha">Agatha</el-checkbox>
                    <el-checkbox value="s3">S3</el-checkbox>
                </el-checkbox-group>
            </el-form-item>
            <div class="mb-1 label--text">Lease start date</div>
            <el-form-item prop="leaseStartDate">
                <el-date-picker
                    v-model="form.leaseStartDate"
                    type="date"
                    placeholder="Lease start date"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                >
                </el-date-picker>
            </el-form-item>
            <div class="mb-1 label--text">Expired date</div>
            <el-form-item prop="expiredDate">
                <el-date-picker
                    v-model="form.expiredDate"
                    type="date"
                    placeholder="Expired date"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                >
                </el-date-picker>
            </el-form-item>
            <el-form-item prop="isLicensed">
                <el-switch v-model="form.isLicensed" active-text="Is active" />
            </el-form-item>
            <el-form-item prop="isAllowedWithoutLicenseKey">
                <el-switch
                    v-model="form.isAllowedWithoutLicenseKey"
                    active-text="Allowed without license key"
                />
            </el-form-item>
            <el-form-item class="text-center">
                <el-button
                    type="primary"
                    color="primary"
                    :loading="loading"
                    @click="submit"
                    >Submit</el-button
                >
            </el-form-item>
        </el-form>
    </AppSection>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, reactive, toRefs } from 'vue';
import { License } from '../../types/License';
import AppSection from '@/components/AppSection.vue';

const CreateEditLicenseForm = defineComponent({
    components: { AppSection },
    props: {
        license: {
            type: Object as PropType<License>,
            default: () => {
                return {
                    organizationName: null,
                    maxNumberOfAccessedApisPerOrganization: 0,
                    maxFilesSizePerOrganization: 0,
                    maxNumberOfEp2asVueUsers: 0,
                    maxNumberOfSubmissionUnits: 0,
                    allowedEctdVersions: ['v3.2.2', 'v4.0'],
                    allowedRemoteRepositories: [
                        'wfs_smb_v2_v3',
                        'sharepoint',
                        'sharepoint_entraid',
                    ],
                    leaseStartDate: '',
                    expiredDate: '',
                    isLicensed: true,
                    isAllowedWithoutLicenseKey: false,
                };
            },
        },
    },
    emits: ['submit'],
    setup(props, context) {
        console.log(props.license);
        const formData = reactive({
            form: { ...props.license } as any,
            formRef: ref(),
            rules: {
                organizationName: [
                    {
                        required: true,
                        message: 'Please input Organization name',
                        trigger: 'blur',
                    },
                ],
                maxNumberOfAccessedApisPerOrganization: [
                    {
                        type: 'number',
                        required: true,
                        message: 'Please input the number',
                        trigger: 'blur',
                    },
                    {
                        validator: (rule: any, value: any) => {
                            if (
                                Number(value) >= -1 &&
                                Number(value) <= 2147483647
                            ) {
                                return true;
                            } else {
                                return false;
                            }
                        },
                        message: 'The value must be -1 or higher.',
                        trigger: ['blur', 'change'],
                    },
                    {
                        pattern: /^(0|-*[1-9][0-9]*)$/,
                        message: 'The value must be integer',
                        trigger: ['blur', 'change'],
                    },
                ],
                maxFilesSizePerOrganization: [
                    {
                        type: 'number',
                        required: true,
                        message: 'Please input the number',
                        trigger: 'blur',
                    },
                    {
                        validator: (rule: any, value: any) => {
                            if (
                                Number(value) >= -1 &&
                                Number(value) <= 2147483647
                            ) {
                                return true;
                            } else {
                                return false;
                            }
                        },
                        message: 'The value must be -1 or higher.',
                        trigger: ['blur', 'change'],
                    },
                    {
                        pattern: /^(0|-*[1-9][0-9]*)$/,
                        message: 'The value must be integer',
                        trigger: ['blur', 'change'],
                    },
                ],
                maxNumberOfEp2asVueUsers: [
                    {
                        type: 'number',
                        required: true,
                        message: 'Please input the number',
                        trigger: 'blur',
                    },
                    {
                        validator: (rule: any, value: any) => {
                            if (
                                Number(value) >= -1 &&
                                Number(value) <= 2147483647
                            ) {
                                return true;
                            } else {
                                return false;
                            }
                        },
                        message: 'The value must be -1 or higher.',
                        trigger: ['blur', 'change'],
                    },
                    {
                        pattern: /^(0|-*[1-9][0-9]*)$/,
                        message: 'The value must be integer',
                        trigger: ['blur', 'change'],
                    },
                ],
                maxNumberOfSubmissionUnits: [
                    {
                        type: 'number',
                        required: true,
                        message: 'Please input the number',
                        trigger: 'blur',
                    },
                    {
                        validator: (rule: any, value: any) => {
                            if (
                                Number(value) >= -1 &&
                                Number(value) <= 2147483647
                            ) {
                                return true;
                            } else {
                                return false;
                            }
                        },
                        message: 'The value must be -1 or higher.',
                        trigger: ['blur', 'change'],
                    },
                    {
                        pattern: /^(0|-*[1-9][0-9]*)$/,
                        message: 'The value must be integer',
                        trigger: ['blur', 'change'],
                    },
                ],
                leaseStartDate: [
                    {
                        type: 'date',
                        required: true,
                        message: 'Please select the lease start date',
                        trigger: 'change',
                    },
                    {
                        validator: (rule: any, value: any) => {
                            if (!value || !formData.form.expiredDate) return;
                            const d1 = new Date(value);
                            const d2 = new Date(formData.form.expiredDate);
                            return d1 < d2;
                        },
                        message:
                            'The lease start date must be before the expired date',
                        trigger: 'change',
                    },
                ],
                expiredDate: [
                    {
                        type: 'date',
                        required: true,
                        message: 'Please select the expired date',
                        trigger: 'change',
                    },
                    {
                        validator: (rule: any, value: any) => {
                            if (!value || !formData.form.leaseStartDate) return;
                            const d1 = new Date(value);
                            const d2 = new Date(formData.form.leaseStartDate);
                            return d1 > d2;
                        },
                        message:
                            'The expired date must be after the lease start date',
                        trigger: 'change',
                    },
                ],
            } as any,
        });

        const loading = ref(false);

        const submit = () => {
            formData.formRef.validate((valid: boolean) => {
                if (valid) {
                    loading.value = true;
                    context.emit('submit', formData.form);
                } else {
                    return false;
                }
            });
        };

        return {
            ...toRefs(formData),
            submit,
            loading,
        };
    },
});
export default CreateEditLicenseForm;
</script>
